import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { firstValueFrom } from 'rxjs';
import { HistoryEntry, StoreHistoryService } from 'src/app/services/store-history/store-history.service';
import { relativeTimeFrom } from 'src/app/shared/relative-time';
import { ChangesState, isActionable } from '../history-sheet.types';
import type { UndoToConfirmData, UndoToConfirmDialogComponent } from '../undo-to-confirm/undo-to-confirm.component';

/** Rendered while the first page is in flight. Enough rows to fill the panel, not so many it lies. */
const SKELETON_ROWS = [0, 1, 2, 3, 4];

@Component({
  templateUrl: './changes-tab.component.html',
  standalone: true,
  selector: 'app-history-changes-tab',
  imports: [
    NgTemplateOutlet,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
})
export class HistoryChangesTabComponent {
  private readonly storeHistory = inject(StoreHistoryService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly snackbar = inject(MatSnackBar);

  readonly state = input.required<ChangesState>();

  /** Asks the sheet to switch tabs — a blocked entry's only way forward is a restore. */
  readonly showSnapshots = output<void>();
  readonly retry = output<void>();
  /** Any action inside the tab, so the sheet's `abandoned` event can tell reading from doing. */
  readonly interacted = output<void>();

  readonly skeletonRows = SKELETON_ROWS;
  readonly loadingMore = signal(false);
  readonly busy = signal(false);

  readonly canUndo = computed(() => this.storeHistory.canUndo());
  readonly canRedo = computed(() => this.storeHistory.canRedo());
  readonly snapshotAt = computed(() => this.storeHistory.snapshotHeader()?.at ?? undefined);
  readonly hasMore = computed(() => this.storeHistory.savedHasMore());

  /** Newest first, which is the opposite of how the draft stack is kept. */
  readonly draftEntries = computed(() => [...this.storeHistory.draft()].reverse());
  readonly savedEntries = computed(() => this.storeHistory.saved());

  /** The one entry the primary Undo would reverse. */
  private readonly nextUndo = computed(() => this.storeHistory.nextUndo());

  isPrimary(entry: HistoryEntry): boolean {
    return this.nextUndo()?.id === entry.id;
  }

  /** Older reversible entries offer "Undo to here"; the primary row already has plain Undo. */
  canUndoTo(entry: HistoryEntry): boolean {
    return isActionable(entry) && !this.isPrimary(entry);
  }

  isSystem(entry: HistoryEntry): boolean {
    return entry.actor.type === 'system';
  }

  actorKey(entry: HistoryEntry): string {
    return `store-history.actor.${entry.actor.type}`;
  }

  blockedReasonKey(entry: HistoryEntry): string {
    return `store-history.blocked.${entry.blockedReason ?? 'unsupported'}`;
  }

  relativeTime(entry: HistoryEntry): string {
    return relativeTimeFrom(entry.at, this.translate.getCurrentLang());
  }

  absoluteTime(entry: HistoryEntry): string {
    return entry.at.toLocaleString(this.translate.getCurrentLang());
  }

  async onUndo(): Promise<void> {
    this.interacted.emit();
    await this.run(() => this.storeHistory.undo());
  }

  async onRedo(): Promise<void> {
    this.interacted.emit();
    await this.run(() => this.storeHistory.redo());
  }

  /**
   * Reverses everything down to and including `entry`.
   *
   * The count is taken from the rendered list rather than from an index: a system entry between two
   * merchant ones is skipped by the engine, and a confirm that promised "4 actions" while reversing
   * 3 would be worse than no confirm.
   */
  async onUndoTo(entry: HistoryEntry): Promise<void> {
    this.interacted.emit();

    const component = await import('../undo-to-confirm/undo-to-confirm.component').then(
      (module) => module.UndoToConfirmDialogComponent,
    );

    const confirmed = await firstValueFrom(
      this.dialog
        .open<UndoToConfirmDialogComponent, UndoToConfirmData, boolean>(component, {
          data: { entry, count: this.reversibleCountTo(entry) },
          width: '420px',
        })
        .afterClosed(),
    );

    if (!confirmed) return;

    await this.run(() => this.storeHistory.undoTo(entry));
  }

  async onLoadMore(): Promise<void> {
    this.loadingMore.set(true);

    try {
      await this.storeHistory.loadMore();
    } finally {
      this.loadingMore.set(false);
    }
  }

  private reversibleCountTo(entry: HistoryEntry): number {
    const list = entry.tier === 'draft' ? this.draftEntries() : this.savedEntries();
    const index = list.findIndex((candidate) => candidate.id === entry.id);

    if (index < 0) return 1;

    return list.slice(0, index + 1).filter(isActionable).length;
  }

  /**
   * A refused undo is reported where the merchant is looking, not swallowed.
   *
   * The sheet's own error state belongs to a failed *load* — the list is still on screen and still
   * correct here, so replacing it with an error page would be a lie about what failed.
   */
  private async run(action: () => Promise<void>): Promise<void> {
    this.busy.set(true);

    try {
      await action();
    } catch (error) {
      captureException(error);
      this.snackbar.open(
        (error as Error)?.message ?? this.translate.instant(_('error.error-occured')),
        this.translate.instant(_('common.close')),
        { panelClass: 'snackbar-error' },
      );
    } finally {
      this.busy.set(false);
    }
  }

  /**
   * Public because the heading interpolates it through the `translate` *pipe* rather than
   * `translate.instant`: a `computed` over `instant` memoises the string it produced in whatever
   * language was active when it first ran, and never re-renders when the merchant switches.
   */
  formatDate(at: Date): string {
    return at.toLocaleDateString(this.translate.getCurrentLang(), { day: 'numeric', month: 'short' });
  }
}
